import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { spaceGroupsKeys } from 'src/features/space-groups/api/query-keys/space-groups';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { isApiErrorMessage } from '../../../../../api/utils/is-api-error-message';
import { ApiErrorMessage } from '../../../../booking-rules/api/types/api-error-message';
import { employeesGroupsApiErrorsTranslations } from '../../../constants/api-errors-translations';
import { unassignSpaceGroupFromEmployeeGroup } from '../../lib/employees-group-space-groups';
import { employeesGroupsKeys } from '../../query-keys/employees-groups';
import { UnassignSpaceGroupFromEmployeeGroupPayload } from '../../types/payload/unassign-space-group-from-employee-group.payload';

export const useUnassignSpaceGroupFromEmployeeGroup = (
  onSuccess?: () => void,
) => {
  const { t } = useTranslation(['employeesGroups', 'common']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UnassignSpaceGroupFromEmployeeGroupPayload) =>
      unassignSpaceGroupFromEmployeeGroup(payload),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({
          queryKey: employeesGroupsKeys.lists(),
        });

        queryClient.invalidateQueries({
          queryKey: spaceGroupsKeys.lists(),
        });
      });

      toast.success(
        t(
          'employeesGroups:spaceGroups.unassignSpaceGroupFromEmployeeGroupDialog.successMessage',
        ),
      );

      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          employeesGroupsApiErrorsTranslations,
        )
      ) {
        toast.error(t(employeesGroupsApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(
        error?.response?.data?.message ||
          t('common:errorMessages.somethingWentWrong'),
      );
    },
  });
};
